import type { FC } from 'react';
import type { PhotoProps } from './types';

import { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import { Wrapper } from '../Common';

import {
  ApiService,
  baseMediaEndpoint,
  defaultWebValue,
  filterBackgroundColor,
  sanitizeRichText,
  generateAccessKey
} from 'shared/helpers/index';
import { useSales, useWebsiteEditor } from 'shared/hooks/index';

import { HoverableDropzone } from '../HoverableDropzone';
import { Image as ImageComp } from 'shared/components/Common';
import useIsEmpty from 'shared/hooks/useIsEmpty';

const Photo = styled.div`
  padding: 80px 0;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
  padding: 60px 0;
`}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
  padding: 60px 15px;
`}
`;

const Container = styled(Wrapper)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 40px;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    flex-direction: column-reverse;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    flex-direction: column-reverse;
  `}
`;

const TitleContainer = styled.div`
  width: 588px;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
      width: 100%;
      margin-top: 30px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
    margin-top: 0px;
  `}
`;

const Title = styled.h2<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $headlineFont } }) => $headlineFont};
  font-weight: 600;
  margin-bottom: 16px;
  position: relative;
  min-height: inherit;
  line-height: 1.35em;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    min-height: 80px;
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      width: 100%;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const Text = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  line-height: 1.35em;
  position: relative;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    min-height: 50px;
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      width: 100%;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const ImageInner = styled.div`
  width: auto;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: -webkit-fill-available;
  `}
`;

const imageStyles = css`
  width: 640px;
  height: 428px;
  border: none;
  position: relative;
  overflow: hidden;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 532px !important;
    height: 356px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100% !important;
  `}

  & img {
    border-radius: 5px;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center center;
  }
`;

export const V7: FC<PhotoProps> = (props) => {
  const { meta, hidden, textColor, benefits = [], customBackgroundColor, order } = props;

  const {
    editable,
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    websiteAssembler
  } = useWebsiteEditor();

  const { checkBenefits: checkTitleBenefits } = useIsEmpty(meta, 'title', true);
  const { checkBenefits: checkContentBenefits } = useIsEmpty(meta, 'content', true);

  const { salesPageId, updateImageMeta, isDashboard } = useSales();

  const [loading, setLoading] = useState(false);
  const [resize, setResize] = useState({
    width: meta?.['width'] ?? 0,
    height: meta?.['height'] ?? 0
  });

  // Accessibility improvements
  const titleAccessKey = useMemo(() => generateAccessKey('pht', order), [order]);
  const contentAccessKey = useMemo(() => generateAccessKey('phc', order), [order]);
  const imageAccessKey = useMemo(() => generateAccessKey('phi', order), [order]);
  const skipLinkId = useMemo(() => `skip-to-photo-v7-${meta['accessorKey'] || 'photo'}`, [meta]);

  const handleEditError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Landscape photo section edit error: ${error instanceof Error ? error.message : 'Edit action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const image = useMemo(() => {
    if (benefits.length) {
      return {
        ...benefits[0],
        title: {
          ...benefits[0].title,
          isEmpty: checkTitleBenefits(0)
        },
        content: {
          ...benefits[0].content,
          isEmpty: checkContentBenefits(0)
        }
      };
    }
    return {
      title: {
        ...defaultWebValue,
        isEmpty: checkTitleBenefits(0)
      },
      content: {
        ...defaultWebValue,
        isEmpty: checkContentBenefits(0)
      }
    };
  }, [benefits, checkContentBenefits, checkTitleBenefits]);

  const aboutMeImage = useMemo(() => {
    if (meta['image'] || meta['image_1']) {
      if (baseMediaEndpoint?.includes('localhost'))
        return `${baseMediaEndpoint}${meta['image'] || meta['image_1']}`;
      return meta['image'];
    }
    return '/img/lead_bg.png';
  }, [meta]);

  const handleUpload = async (file: File) => {
    setLoading(true);
    if (!meta['accessorKey'] || !editable) return;

    try {
      const formData = new FormData();
      formData.append('type', meta['accessorKey']);
      formData.append('file', file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const width = img.width;
          const height = img.height;
          setResize({ width, height });
          updateImageMeta(meta['accessorKey'], width, 'width');
          updateImageMeta(meta['accessorKey'], height, 'height');
        };
        img.src = e.target?.result as string;
      };

      reader.readAsDataURL(file);
      const response = await ApiService.post(`/sales/sales-page/${salesPageId}/media/`, formData);
      updateImageMeta(meta['accessorKey'], response.data.file, 'image');
    } catch (error) {
      handleEditError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Photo
      className={containerClassname}
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1,
        backgroundColor: customBackgroundColor || '#fff'
      }}
      aria-label="Landscape photo section with text and horizontal image"
      role="region">
      {/* Skip link for keyboard navigation */}
      <span className="sr-only">
        <a href={`#${skipLinkId}`}>Skip to landscape photo section</a>
      </span>

      <Container id={skipLinkId}>
        <TitleContainer role="region" aria-label="Landscape photo section text content">
          <Title
            id={image?.title?.id}
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(image?.title?.val, editable) }}
            style={{ color: filterBackgroundColor(customBackgroundColor || '#fff', textColor) }}
            isEmpty={image.title.isEmpty}
            {...(isDashboard
              ? {
                  onClick: (e: React.MouseEvent<HTMLElement>) => {
                    try {
                      updateActiveElement(e);
                    } catch (error) {
                      handleEditError(error);
                    }
                  },
                  onBlur: resetActiveElement,
                  'data-accessor-parent': meta['accessorKey'],
                  'data-accessorkey': 'title',
                  'data-benefits': true,
                  'data-placeholder': '+',
                  editable: true
                }
              : {
                  'aria-label':
                    image.title.isEmpty || !image?.title?.val?.trim()
                      ? 'Landscape photo section title'
                      : undefined,
                  accessKey: titleAccessKey,
                  title: `Press Alt+${titleAccessKey} to focus this section title`,
                  tabIndex: 0
                })}
          />

          <Text
            id={image?.content?.id}
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(image?.content?.val, editable) }}
            style={{ color: filterBackgroundColor(customBackgroundColor || '#fff', textColor) }}
            isEmpty={image.content.isEmpty}
            {...(isDashboard
              ? {
                  onClick: (e: React.MouseEvent<HTMLElement>) => {
                    try {
                      updateActiveElement(e);
                    } catch (error) {
                      handleEditError(error);
                    }
                  },
                  onBlur: resetActiveElement,
                  'data-accessor-parent': meta['accessorKey'],
                  'data-accessorkey': 'content',
                  'data-benefits': true,
                  'data-placeholder': '+',
                  editable: true
                }
              : {
                  role: 'textbox',
                  'aria-multiline': 'true',
                  'aria-label':
                    image.content.isEmpty || !image?.content?.val?.trim()
                      ? 'Landscape photo section description'
                      : undefined,
                  'aria-describedby': image?.title?.id,
                  accessKey: contentAccessKey,
                  title: `Press Alt+${contentAccessKey} to focus this section description`,
                  tabIndex: 0
                })}
          />
        </TitleContainer>

        <ImageInner
          role="img"
          aria-label="Landscape format photo"
          {...(!isDashboard && {
            accessKey: imageAccessKey,
            title: `Press Alt+${imageAccessKey} to focus the landscape photo`,
            tabIndex: 0
          })}>
          <HoverableDropzone
            isSalePage
            css={imageStyles as never}
            disabled={!editable}
            allowedExtensions={['image/png', 'image/jpg', 'image/jpeg']}
            enableLinks
            updateMeta={(value) => updateImageMeta(meta['accessorKey'], value, 'image_link')}
            onUpload={handleUpload}
            loading={loading}
            alreadyUploaded={Boolean(aboutMeImage)}
            imageFile={aboutMeImage}
            resizeSelector={editable}
            handleResize={({ width, height }) => {
              setResize({ width, height });
              updateImageMeta(meta['accessorKey'], width, 'width');
              updateImageMeta(meta['accessorKey'], height, 'height');
            }}
            initialPosition={{
              x: meta['x_1'] || 0,
              y: meta['y_1'] || 0,
              width: meta['d_width_1'],
              height: meta['d_height_1']
            }}
            meta={meta}
            positionEnabled
            handlePosition={(width: number, height: number, x: number, y: number) => {
              updateImageMeta(meta['accessorKey'], x, 'x_1');
              updateImageMeta(meta['accessorKey'], y, 'y_1');
              updateImageMeta(meta['accessorKey'], width, 'd_width_1');
              updateImageMeta(meta['accessorKey'], height, 'd_height_1');
            }}
            imageAspectRatio={{
              ratio: '2x1',
              pixels: '640x340'
            }}>
            <ImageComp
              src={aboutMeImage}
              width={resize.width || 500}
              height={resize.height || 500}
              alt="Landscape format photo displaying wide horizontal content alongside text description"
              role="img"
              aria-describedby={image?.content?.id}
            />
          </HoverableDropzone>
        </ImageInner>
      </Container>
    </Photo>
  );
};

V7.displayName = 'PhotoV7';
